import React from 'react';
import { CalendarClockIcon } from 'lucide-react';
import { RevealItem } from '../common/Reveal';
import { PageHeader } from '../layout/PageHeader';
import { TeamSelect } from '../common/TeamSelect';
import { SearchInput } from '../common/SearchInput';
import { Pagination } from '../common/Pagination';
import { Button } from '../common/Button';
import { StateLegend } from '../floor/StateLegend';
import { DataSourceNotice } from '../floortime/DataSourceNotice';
import { FloorRangePicker } from '../floortime/FloorRangePicker';
import { FloorTotals } from '../floortime/FloorTotals';
import { FloorTable } from '../floortime/FloorTable';
import { FLOOR_PAGE_SIZE, useFloorTime } from '../floortime/useFloorTime';
import { formatHhMm, formatPercent } from '../../utils/format';
import { teamLabel } from '../../utils/teams';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';
export function FloorView({
  hub
}: {
  hub: CallsInsightHubState;
}) {
  const ft = useFloorTime(hub);
  const multiDay = ft.range.dates.length > 1;
  const openAgent = (id: string) => {
    if (ft.range.dates.length > 0) hub.setFloorDate(ft.range.dates[ft.range.dates.length - 1]);
    hub.setSelectedId(id);
  };
  return <>
      <PageHeader title="Floor Time" subtitle={`${hub.team === 'All' ? 'All teams' : teamLabel(hub.team)} · ${ft.agentCount} agents · ${ft.range.label}`} data-id="element-730" data-mp="3o5qasoiwcire" />

      <RevealItem className="mb-4" data-id="element-731" data-mp="1qorl6t71h1e">
        <DataSourceNotice data-id="element-732" data-mp="1tt18jpfh4zg" />
      </RevealItem>

      <RevealItem className="mb-6 flex flex-wrap items-center gap-3" data-id="element-733" data-mp="5s0j5hvmocc8">
        <FloorRangePicker preset={ft.preset} onPresetChange={ft.setPreset} custom={ft.custom} onCustomChange={ft.setCustom} data-id="element-734" data-mp="asehg87fg7x2" />
        <TeamSelect value={hub.team} onChange={hub.setTeam} data-id="element-735" data-mp="fq6k9x9m1eg1" />
      </RevealItem>

      <RevealItem data-id="element-736" data-mp="2ck1b6hipegx8">
        <FloorTotals totals={ft.totals} agentDays={ft.agentDays} hasData={ft.hasData} perDay={multiDay} data-id="element-737" data-mp="2ckjic96wmfjx" />
      </RevealItem>

      <RevealItem className="mt-6" data-id="element-738" data-mp="2hj4f1ymy6h3m">
        {ft.hasData ? <FloorTable rows={ft.pageRows} sort={ft.sort} onSort={ft.toggleSort} onSelect={openAgent} header={<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-740" data-mp="2jg2w8xuqpuyv">
                <div data-id="element-741" data-mp="2egzq1a30xipb">
                  <h2 className="text-base font-semibold text-ink" data-id="element-742" data-mp="2egq9ed3vcnwa">Agent floor time</h2>
                  <p className="mt-0.5 text-xs text-muted" data-id="element-743" data-mp="2sg9526fhu6rp">
                    {multiDay ? `Totals across ${ft.range.dates.length} days` : 'One day'} · Team logged in{' '}
                    <span className="font-semibold tabular-nums text-ink" data-id="element-744" data-mp="2odcriu4t90um">{formatHhMm(ft.totals.loggedIn)}</span> · Team focus{' '}
                    <span className="font-semibold tabular-nums text-ink" data-id="element-745" data-mp="2x6zcdp7hcjp1">{formatPercent(ft.totals.focus)}</span>
                  </p>
                  <StateLegend hints className="mt-2" data-id="element-746" data-mp="3c59s1p2t86gf" />
                </div>
                <SearchInput value={ft.query} onChange={ft.setQuery} placeholder="Search agent or extension..." label="Search agents" data-id="element-747" data-mp="376q5bmge6qn0" />
              </div>} footer={ft.sortedRows.length === 0 ? <p className="py-2 text-center text-sm text-muted" data-id="element-748" data-mp="3us94jnj9uab6">No agents match "{ft.query}".</p> : <Pagination page={ft.page} pageCount={ft.pageCount} total={ft.sortedRows.length} pageSize={FLOOR_PAGE_SIZE} onChange={ft.setPage} data-id="element-749" data-mp="3gvneuztk7rru" />} data-id="element-739" data-mp="29h4z56j90t49" /> : <div className="flex flex-col items-center rounded-2xl border border-line bg-surface px-6 py-14 text-center shadow-card" data-id="element-750" data-mp="371n1ybs1di8k">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-soft text-accent" data-id="element-751" data-mp="2y3krl5mexd8m">
              <CalendarClockIcon className="h-5 w-5" aria-hidden data-id="element-752" data-mp="332xlubfm5ml8" />
            </span>
            <p className="mt-3 text-sm font-semibold text-ink" data-id="element-753" data-mp="2o4c1woykyks2">Today's Five9 report isn't in yet</p>
            <p className="mt-1 max-w-md text-sm text-muted" data-id="element-754" data-mp="2t3yeqxahxz0c">
              Five9 sends the daily agent state report each morning, so today's floor time will show up tomorrow.
            </p>
            <Button className="mt-4" onClick={() => ft.setPreset('yesterday')} data-id="element-755" data-mp="3rezor4yzynk8">
              View yesterday
            </Button>
          </div>}
      </RevealItem>
    </>;
}